import {
  RefObject,
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import moment from 'moment';
import { reduce, filter, isArray, includes, isEqual, has } from 'lodash';
import { useTabContext } from '@mui/lab/TabContext';

import {
  useSubscriptionFeatures,
  useParseLocationQuery,
  useFiltersValues,
} from '~hooks/index';
import { getItemValue, formatterDate } from '~utils/index';
import { FiltersValuesReturn } from '~hooks/useFiltersValues';
import { ItemFilter } from '~services/item/types';
import { DriverCompanyBase } from '~services/driver/types';
import { VehicleBase } from '~services/vehicle/types';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { RouteUserBasic } from '~services/route/types';
import { Nullable, SelectOption } from '~globals/types';
import {
  RouteItemStateType,
  ItemStateTypes,
  PlanFeaturesTypes,
} from '~globals/types/enums';
import { OrdersPanelsId } from '~modules/Orders/types';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';

export interface OrdersListQueryParamsFilter {
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  expirationDate?: Date;
  state?: RouteItemStateType;
  itemState?: ItemStateTypes;
  drivers?: string | string[];
  vehicles?: string | string[];
  services?: string | string[];
  zones?: string | string[];
  unit1Gt?: number;
  unit1Lt?: number;
  unit2Gt?: number;
  unit2Lt?: number;
  priceGt?: number;
  priceLt?: number;
  assignedUser?: string;
}

export interface OrdersListFilters {
  dateFrom: Nullable<Date>;
  dateTo: Nullable<Date>;
  search: string;
  expirationDate: Nullable<Date>;
  state: Nullable<RouteItemStateType>;
  itemState: Nullable<ItemStateTypes>;
  drivers: Nullable<DriverCompanyBase[]>;
  vehicles: Nullable<VehicleBase[]>;
  services: Nullable<SelectOption[]>;
  zones: Nullable<LogisticZoneBase[]>;
  unit1Gt: string;
  unit1Lt: string;
  unit2Gt: string;
  unit2Lt: string;
  priceGt: string;
  priceLt: string;
  assignedUser: Nullable<RouteUserBasic>;
}

type OrdersListFiltersAppliedType = 'queryParams' | 'filters';

type OrdersListFiltersApplied = Record<
  keyof OrdersListFilters,
  OrdersListFiltersAppliedType
>;

export type OrdersListAutocompleteMultipleRef<T> = CustomAutocompleteRef<
  T,
  true
>;

export type OrdersListAutocompleteSingleRef<T> = CustomAutocompleteRef<
  T,
  false
>;

export interface OrdersListParamsSelectData<T, M extends boolean> {
  autocompleteRef: RefObject<
    M extends true
      ? OrdersListAutocompleteMultipleRef<T>
      : OrdersListAutocompleteSingleRef<T>
  >;
  loading: boolean;
  list: T[];
}

export interface OrdersListParamsSelects {
  drivers: OrdersListParamsSelectData<DriverCompanyBase, true>;
  vehicles: OrdersListParamsSelectData<VehicleBase, true>;
  services: OrdersListParamsSelectData<SelectOption, true>;
  logisticsZones: OrdersListParamsSelectData<LogisticZoneBase, true>;
  users: OrdersListParamsSelectData<RouteUserBasic, false>;
}

interface OrdersListParams {
  initialFilters: OrdersListFilters;
  defaultStates: { routeItemState: RouteItemStateType[] };
  selectsData: OrdersListParamsSelects;
}

export interface OrdersListReturn
  extends FiltersValuesReturn<OrdersListFilters> {
  parseFiltersRequest: (
    currentFilters?: Partial<OrdersListFilters>,
  ) => ItemFilter;
  filtersActives: OrdersListFilters;
  setFiltersActives: (newFiltersActives: OrdersListFilters) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilterActiveByKey: (key: string, newVal: any) => void;
  clearFiltersActives: () => void;
}

export const useOrdersListFilters = ({
  initialFilters,
  defaultStates,
  selectsData,
}: OrdersListParams): OrdersListReturn => {
  const tabContext = useTabContext();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const hasAccesLimitNotPro = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
    [hasAccessToFeature],
  );

  const hasAccesMgmZones = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.MgmZones),
    [hasAccessToFeature],
  );

  const validateTabs = useCallback(
    (tabs: OrdersPanelsId[]) => {
      const activeTab = tabContext?.value as OrdersPanelsId;

      return includes(tabs, activeTab);
    },
    [tabContext?.value],
  );

  const {
    filters,
    setFilterByKey,
    clearFilters,
    handleChangeFilters,
    ...restFiltersData
  } = useFiltersValues(initialFilters);

  const [filtersActives, setFiltersActives] =
    useState<OrdersListFilters>(filters);

  const setFilterActiveByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, newVal: any) => {
      setFiltersActives((prevActiveFilters) => ({
        ...prevActiveFilters,
        [key]: newVal,
      }));
    },
    [],
  );

  const clearFiltersActives = useCallback(() => {
    setFiltersActives(initialFilters);
  }, [initialFilters]);

  const queryFilters = useParseLocationQuery<OrdersListQueryParamsFilter>();

  const [filtersApplied, setFilterApplied] = useState<OrdersListFiltersApplied>(
    () => {
      const builder = reduce(
        initialFilters,
        (acc, _, key) => {
          const keyTyped = key as keyof OrdersListFilters;

          acc[keyTyped] = has(queryFilters, keyTyped)
            ? 'queryParams'
            : 'filters';

          return acc;
        },
        {} as OrdersListFiltersApplied,
      );

      return builder;
    },
  );

  const handleChangeFiltersApplied = useCallback(
    (newFilters: OrdersListFilters) => {
      const newFiltersApplied = reduce(
        newFilters,
        (acc, _, key) => {
          const keyTyped = key as keyof OrdersListFilters;

          acc[keyTyped] = 'filters';

          return acc;
        },
        {} as OrdersListFiltersApplied,
      );

      handleChangeFilters?.(newFilters);
      setFilterApplied(newFiltersApplied);
    },
    [handleChangeFilters],
  );

  const setFilterAppliedByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, value: any) => {
      setFilterByKey(key, value);

      const keyTyped = key as keyof OrdersListFilters;

      setFilterApplied((prevFiltersApplied) => ({
        ...prevFiltersApplied,
        [keyTyped]: 'filters',
      }));
    },
    [setFilterByKey],
  );

  const handleChangeInputFilterApplied = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target ?? event.currentTarget;

      setFilterAppliedByKey(name, value);
    },
    [setFilterAppliedByKey],
  );

  const clearFiltersApplied = useCallback(
    (currentFilters?: OrdersListFilters) => {
      clearFilters(currentFilters);

      let newFiltersApplied: OrdersListFiltersApplied = {
        dateFrom: 'filters',
        dateTo: 'filters',
        search: 'filters',
        expirationDate: 'filters',
        state: 'filters',
        itemState: 'filters',
        drivers: 'filters',
        vehicles: 'filters',
        services: 'filters',
        zones: 'filters',
        unit1Gt: 'filters',
        unit1Lt: 'filters',
        unit2Gt: 'filters',
        unit2Lt: 'filters',
        priceGt: 'filters',
        priceLt: 'filters',
        assignedUser: 'filters',
      };

      if (currentFilters) {
        newFiltersApplied = reduce(
          currentFilters,
          (acc, _, key) => {
            const keyTyped = key as keyof OrdersListFilters;

            acc[keyTyped] = 'filters';

            return acc;
          },
          {} as OrdersListFiltersApplied,
        );
      }
      setFilterApplied(newFiltersApplied);
    },
    [clearFilters, setFilterApplied],
  );

  const getValueQueryParams = useCallback(
    <Key extends keyof OrdersListQueryParamsFilter>(
      key: Key,
    ): OrdersListQueryParamsFilter[Key] => getItemValue(queryFilters, key),
    [queryFilters],
  );

  const getFilterAppliedByKey = useCallback(
    <Key extends keyof OrdersListFiltersApplied>(
      key: Key,
    ): OrdersListFiltersApplied[Key] => getItemValue(filtersApplied, key),
    [filtersApplied],
  );

  const getValueCurrentFilter = useCallback(
    <Key extends keyof OrdersListFilters>(key: Key): OrdersListFilters[Key] =>
      getItemValue(filters, key),
    [filters],
  );

  const setValueFilterByQueryParam = useCallback(
    ({
      key,
      excludeTabs,
      transformValue,
    }: {
      key: keyof OrdersListQueryParamsFilter;
      excludeTabs?: OrdersPanelsId[];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformValue?: (currentValue: any) => void;
    }) => {
      const isExcludeTab = excludeTabs ? validateTabs(excludeTabs) : false;

      if (isExcludeTab) return;

      const currentValueByQueryParam = getValueQueryParams(key);
      const isValidTabs = excludeTabs ? !validateTabs(excludeTabs) : true;

      if (
        currentValueByQueryParam !== undefined &&
        currentValueByQueryParam !== null &&
        isValidTabs
      ) {
        const value = transformValue
          ? transformValue(currentValueByQueryParam)
          : currentValueByQueryParam;

        setFilterByKey(key, value);
        setFilterActiveByKey(key, value);
      }
    },
    [validateTabs, getValueQueryParams, setFilterByKey, setFilterActiveByKey],
  );

  useEffect(() => {
    setValueFilterByQueryParam({ key: 'search' });
    setValueFilterByQueryParam({ key: 'state' });
    setValueFilterByQueryParam({
      key: 'itemState',
      excludeTabs: ['on-agenda', 'finalized'],
    });
    setValueFilterByQueryParam({
      key: 'priceGt',
      transformValue: (val) => String(val),
    });
    setValueFilterByQueryParam({
      key: 'priceLt',
      transformValue: (val) => String(val),
    });
  }, [setValueFilterByQueryParam]);

  const setDateFilterByQueryParams = useCallback(
    ({
      key,
      excludeTabs,
    }: {
      key: 'dateFrom' | 'dateTo' | 'expirationDate';
      excludeTabs?: OrdersPanelsId[];
    }) => {
      const isExcludeTab = excludeTabs ? validateTabs(excludeTabs) : false;

      if (isExcludeTab) return;

      const currentDateQueryParam = getValueQueryParams(key);

      if (
        currentDateQueryParam !== undefined &&
        currentDateQueryParam !== null
      ) {
        const valDate = new Date(currentDateQueryParam);

        if (valDate.toString() !== 'Invalid Date') {
          setFilterByKey(key, valDate);
          setFilterActiveByKey(key, valDate);
        }
      }
    },
    [validateTabs, getValueQueryParams, setFilterByKey, setFilterActiveByKey],
  );

  useEffect(() => {
    setDateFilterByQueryParams({ key: 'dateFrom', excludeTabs: ['pending'] });
    setDateFilterByQueryParams({ key: 'dateTo', excludeTabs: ['pending'] });
    setDateFilterByQueryParams({
      key: 'expirationDate',
      excludeTabs: ['finalized'],
    });
  }, [setDateFilterByQueryParams]);

  const getOptionValueByExtractorKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      currentOption,
      extractorKey,
    }: {
      currentOption: T;
      extractorKey: string;
    }): string =>
      typeof currentOption !== 'string'
        ? currentOption[extractorKey]
        : currentOption,
    [],
  );

  const getFilteredSelectDataByQueryParam = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>, M extends boolean>({
      currentSelectData,
      currentValueQueryParam,
      extractorKey,
    }: {
      currentSelectData: OrdersListParamsSelectData<T, M>;
      currentValueQueryParam: string | string[];
      extractorKey: string;
    }): T[] => {
      const filteredData = filter(currentSelectData.list, (currentOption) => {
        const currentOptionId = getOptionValueByExtractorKey({
          currentOption,
          extractorKey,
        });

        if (isArray(currentValueQueryParam)) {
          return includes(currentValueQueryParam, currentOptionId);
        }

        return isEqual(currentValueQueryParam, currentOptionId);
      });

      return filteredData;
    },
    [getOptionValueByExtractorKey],
  );

  const setArrayValueByQueryParams = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      key,
      currentSelectData,
      excludeTabs,
      extractorKey,
    }: {
      key: 'drivers' | 'vehicles' | 'services' | 'zones';
      currentSelectData: OrdersListParamsSelectData<T, true>;
      excludeTabs?: OrdersPanelsId[];
      extractorKey: string;
    }) => {
      const isExcludeTab = excludeTabs ? validateTabs(excludeTabs) : false;

      if (isExcludeTab) return;

      const currentArrayQueryParam = getValueQueryParams(key);

      if (
        currentArrayQueryParam !== undefined &&
        currentArrayQueryParam !== null &&
        !currentSelectData.loading
      ) {
        const filteredOptions = getFilteredSelectDataByQueryParam({
          currentSelectData,
          currentValueQueryParam: currentArrayQueryParam,
          extractorKey,
        });

        if (filteredOptions.length > 0) {
          setFilterByKey(key, filteredOptions);
          setFilterActiveByKey(key, filteredOptions);
          currentSelectData.autocompleteRef.current?.setValue(filteredOptions);
        }
      }
    },
    [
      validateTabs,
      getValueQueryParams,
      getFilteredSelectDataByQueryParam,
      setFilterByKey,
      setFilterActiveByKey,
    ],
  );

  useEffect(() => {
    setArrayValueByQueryParams({
      key: 'drivers',
      currentSelectData: selectsData.drivers,
      excludeTabs: ['pending'],
      extractorKey: 'id',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectsData.drivers.list,
    selectsData.drivers.loading,
    setArrayValueByQueryParams,
  ]);

  useEffect(() => {
    setArrayValueByQueryParams({
      key: 'vehicles',
      currentSelectData: selectsData.vehicles,
      excludeTabs: ['pending'],
      extractorKey: 'id',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectsData.vehicles.list,
    selectsData.vehicles.loading,
    setArrayValueByQueryParams,
  ]);

  useEffect(() => {
    if (hasAccesLimitNotPro) {
      setArrayValueByQueryParams({
        key: 'services',
        currentSelectData: selectsData.services,
        extractorKey: 'value',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAccesLimitNotPro,
    selectsData.services.list,
    selectsData.services.loading,
    setArrayValueByQueryParams,
  ]);

  useEffect(() => {
    if (hasAccesMgmZones) {
      setArrayValueByQueryParams({
        key: 'zones',
        currentSelectData: selectsData.logisticsZones,
        extractorKey: 'zoneId',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAccesMgmZones,
    selectsData.logisticsZones.list,
    selectsData.logisticsZones.loading,
    setArrayValueByQueryParams,
  ]);

  useEffect(() => {
    if (hasAccesLimitNotPro) {
      setValueFilterByQueryParam({
        key: 'unit1Gt',
        excludeTabs: ['on-agenda', 'finalized'],
        transformValue: (val) => String(val),
      });
      setValueFilterByQueryParam({
        key: 'unit1Lt',
        excludeTabs: ['on-agenda', 'finalized'],
        transformValue: (val) => String(val),
      });
      setValueFilterByQueryParam({
        key: 'unit2Gt',
        excludeTabs: ['on-agenda', 'finalized'],
        transformValue: (val) => String(val),
      });
      setValueFilterByQueryParam({
        key: 'unit2Lt',
        excludeTabs: ['on-agenda', 'finalized'],
        transformValue: (val) => String(val),
      });
    }
  }, [hasAccesLimitNotPro, setValueFilterByQueryParam]);

  const setArrayUniqueValueByQueryParams = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      key,
      currentSelectData,
      excludeTabs,
      extractorKey,
    }: {
      key: 'assignedUser';
      currentSelectData: OrdersListParamsSelectData<T, false>;
      excludeTabs?: OrdersPanelsId[];
      extractorKey: string;
    }) => {
      const isExcludeTab = excludeTabs ? validateTabs(excludeTabs) : false;

      if (isExcludeTab) return;

      const currentArrayQueryParam = getValueQueryParams(key);

      if (
        currentArrayQueryParam !== undefined &&
        currentArrayQueryParam !== null &&
        !currentSelectData.loading
      ) {
        const filteredOptions = getFilteredSelectDataByQueryParam({
          currentSelectData,
          currentValueQueryParam: currentArrayQueryParam,
          extractorKey,
        });

        if (filteredOptions.length > 0) {
          const currentValue = filteredOptions[0];

          setFilterByKey(key, currentValue);
          setFilterActiveByKey(key, currentValue);
          currentSelectData.autocompleteRef.current?.setValue(currentValue);
        }
      }
    },
    [
      validateTabs,
      getValueQueryParams,
      getFilteredSelectDataByQueryParam,
      setFilterByKey,
      setFilterActiveByKey,
    ],
  );

  useEffect(() => {
    if (hasAccesLimitNotPro) {
      setArrayUniqueValueByQueryParams({
        key: 'assignedUser',
        currentSelectData: selectsData.users,
        extractorKey: 'userId',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAccesLimitNotPro,
    selectsData.users.list,
    selectsData.users.loading,
    setArrayUniqueValueByQueryParams,
  ]);

  const parseDateForRequest = useCallback(
    ({
      currentKey,
      excludeTabs,
      currentDate,
    }: {
      currentKey: 'dateFrom' | 'dateTo' | 'expirationDate';
      excludeTabs?: OrdersPanelsId[];
      currentDate?: Nullable<Date>;
    }): Nullable<string> => {
      const isExcludeTab = excludeTabs ? validateTabs(excludeTabs) : false;

      if (isExcludeTab) return null;

      const currentTypeDateFilterAppliedCurrentDate =
        getFilterAppliedByKey(currentKey);

      const currentDateQueryParam = getValueQueryParams(currentKey) ?? null;

      const currentDateFilter = getValueCurrentFilter(currentKey);

      let date =
        currentTypeDateFilterAppliedCurrentDate === 'queryParams'
          ? currentDateQueryParam
          : currentDateFilter;

      if (currentDate !== undefined) {
        date = currentDate;
      }

      if (date !== null) {
        const formatDate = formatterDate(new Date(date), {
          format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
        });

        return formatDate || null;
      }

      return null;
    },
    [
      getFilterAppliedByKey,
      getValueQueryParams,
      getValueCurrentFilter,
      validateTabs,
    ],
  );

  const parseSearchForRequest = useCallback(
    (currentSearch?: string): Nullable<string> => {
      const currentTypeSearchFilterAppliedCurrentDate =
        getFilterAppliedByKey('search');

      const currentSearchQueryParam = getValueQueryParams('search') ?? null;
      const currentSearchFilter = getValueCurrentFilter('search');

      let search =
        currentTypeSearchFilterAppliedCurrentDate === 'queryParams'
          ? currentSearchQueryParam
          : currentSearchFilter;

      if (currentSearch !== undefined) {
        search = currentSearch;
      }

      return search || null;
    },
    [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilter],
  );

  const parseRouteStatesForRequest = useCallback(
    (currentStates?: Nullable<RouteItemStateType>) => {
      const currentTypeStateFilterAppliedCurrentDate =
        getFilterAppliedByKey('state');

      const currentStateQueryParam = getValueQueryParams('state') ?? null;
      const currentStateFilter = getValueCurrentFilter('state');

      let states =
        currentTypeStateFilterAppliedCurrentDate === 'queryParams'
          ? currentStateQueryParam
          : currentStateFilter;

      if (currentStates !== undefined) {
        states = currentStates;
      }

      if (states !== null && includes(defaultStates.routeItemState, states)) {
        return [states];
      }

      return defaultStates.routeItemState;
    },
    [
      getFilterAppliedByKey,
      getValueQueryParams,
      getValueCurrentFilter,
      defaultStates.routeItemState,
    ],
  );

  const parseItemStateForRequest = useCallback(
    (currentItemState?: Nullable<ItemStateTypes>) => {
      const isExcludeTab = validateTabs(['on-agenda', 'finalized']);

      if (isExcludeTab) return [];

      const currentTypeItemStateFilterAppliedCurrentDate =
        getFilterAppliedByKey('itemState');

      const currentItemStateQueryParam =
        getValueQueryParams('itemState') ?? null;
      const currentItemStateFilter = getValueCurrentFilter('itemState');

      let itemState =
        currentTypeItemStateFilterAppliedCurrentDate === 'queryParams'
          ? currentItemStateQueryParam
          : currentItemStateFilter;

      if (currentItemState !== undefined) {
        itemState = currentItemState;
      }

      return itemState !== null ? [itemState] : [];
    },
    [
      getFilterAppliedByKey,
      getValueCurrentFilter,
      getValueQueryParams,
      validateTabs,
    ],
  );

  const parseDriversForRequest = useCallback(
    (currentDrivers?: Nullable<DriverCompanyBase[]>) => {
      const isExcludeTab = validateTabs(['pending']);

      if (isExcludeTab) return null;

      const currentTypeDriversFilterAppliedDrivers =
        getFilterAppliedByKey('drivers');

      const currentDriversQueryParam = getValueQueryParams('drivers');
      const currentDriversFilter = getValueCurrentFilter('drivers');

      let driversId: Nullable<string[]> = null;

      if (
        currentTypeDriversFilterAppliedDrivers === 'queryParams' &&
        currentDriversQueryParam
      ) {
        driversId = isArray(currentDriversQueryParam)
          ? currentDriversQueryParam
          : [currentDriversQueryParam];
      }

      if (
        currentTypeDriversFilterAppliedDrivers === 'filters' &&
        currentDriversFilter &&
        currentDriversFilter.length > 0
      ) {
        driversId = currentDriversFilter.map(
          (currentDriver) => currentDriver.id,
        );
      }

      if (currentDrivers !== undefined) {
        driversId =
          currentDrivers?.map((currentDriver) => currentDriver.id) ?? null;
      }

      return driversId;
    },
    [
      getFilterAppliedByKey,
      getValueQueryParams,
      getValueCurrentFilter,
      validateTabs,
    ],
  );

  const parseVehiclesForRequest = useCallback(
    (currentVehicles?: Nullable<VehicleBase[]>) => {
      const isExcludeTab = validateTabs(['pending']);

      if (isExcludeTab) return null;

      const currentTypeVehiclesFilterAppliedDrivers =
        getFilterAppliedByKey('vehicles');

      const currentVehiclesQueryParam = getValueQueryParams('vehicles');
      const currentVehiclesFilter = getValueCurrentFilter('vehicles');

      let vehiclesId: Nullable<string[]> = null;

      if (
        currentTypeVehiclesFilterAppliedDrivers === 'queryParams' &&
        currentVehiclesQueryParam
      ) {
        vehiclesId = isArray(currentVehiclesQueryParam)
          ? currentVehiclesQueryParam
          : [currentVehiclesQueryParam];
      }

      if (
        currentTypeVehiclesFilterAppliedDrivers === 'filters' &&
        currentVehiclesFilter &&
        currentVehiclesFilter.length > 0
      ) {
        vehiclesId = currentVehiclesFilter.map(
          (currentVehicle) => currentVehicle.id,
        );
      }

      if (currentVehicles !== undefined) {
        vehiclesId =
          currentVehicles?.map((currentVehicle) => currentVehicle.id) ?? null;
      }

      return vehiclesId;
    },
    [
      getFilterAppliedByKey,
      getValueQueryParams,
      getValueCurrentFilter,
      validateTabs,
    ],
  );

  const parseServicesForRequest = useCallback(
    (currentServices?: Nullable<SelectOption[]>) => {
      if (!hasAccesLimitNotPro) return null;

      const currentTypeServicesFilterAppliedDrivers =
        getFilterAppliedByKey('services');

      const currentServicesQueryParam = getValueQueryParams('services');
      const currentServicesFilter = getValueCurrentFilter('services');

      let services: Nullable<string[]> = null;

      if (
        currentTypeServicesFilterAppliedDrivers === 'queryParams' &&
        currentServicesQueryParam
      ) {
        services = isArray(currentServicesQueryParam)
          ? currentServicesQueryParam
          : [currentServicesQueryParam];
      }

      if (
        currentTypeServicesFilterAppliedDrivers === 'filters' &&
        currentServicesFilter &&
        currentServicesFilter.length > 0
      ) {
        services = currentServicesFilter.map((service) =>
          service.value.toString(),
        );
      }

      if (currentServices !== undefined) {
        services =
          currentServices?.map((currentService) =>
            currentService.value.toString(),
          ) ?? null;
      }

      return services;
    },
    [
      hasAccesLimitNotPro,
      getFilterAppliedByKey,
      getValueQueryParams,
      getValueCurrentFilter,
    ],
  );

  const parseZonesForRequest = useCallback(
    (currentZones?: Nullable<LogisticZoneBase[]>) => {
      if (!hasAccesMgmZones) return null;

      const currentTypeZonesFilterAppliedDrivers =
        getFilterAppliedByKey('zones');

      const currentZonesQueryParam = getValueQueryParams('zones');
      const currentZonesFilter = getValueCurrentFilter('zones');

      let zones: Nullable<string[]> = null;

      if (
        currentTypeZonesFilterAppliedDrivers === 'queryParams' &&
        currentZonesQueryParam
      ) {
        zones = isArray(currentZonesQueryParam)
          ? currentZonesQueryParam
          : [currentZonesQueryParam];
      }

      if (
        currentTypeZonesFilterAppliedDrivers === 'filters' &&
        currentZonesFilter &&
        currentZonesFilter.length > 0
      ) {
        zones = currentZonesFilter.map((zone) => zone.zoneId);
      }

      if (currentZones !== undefined) {
        zones = currentZones?.map((currentZone) => currentZone.zoneId) ?? null;
      }

      return zones;
    },
    [
      hasAccesMgmZones,
      getFilterAppliedByKey,
      getValueQueryParams,
      getValueCurrentFilter,
    ],
  );

  const parseUnitForRequest = useCallback(
    ({
      currentKey,
      excludeTabs,
      currentUnit,
    }: {
      currentKey: 'unit1Gt' | 'unit1Lt' | 'unit2Gt' | 'unit2Lt';
      excludeTabs?: OrdersPanelsId[];
      currentUnit?: string;
    }): Nullable<number> => {
      if (!hasAccesLimitNotPro) return null;

      const isExcludeTab = excludeTabs ? validateTabs(excludeTabs) : false;

      if (isExcludeTab) return null;

      const currentTypeUnitFilterAppliedCurrentDate =
        getFilterAppliedByKey(currentKey);

      const currentUnitQueryParam = getValueQueryParams(currentKey) ?? null;
      const currentUnitFilter = getValueCurrentFilter(currentKey);

      let unit: Nullable<number> = null;

      if (
        currentTypeUnitFilterAppliedCurrentDate === 'queryParams' &&
        currentUnitQueryParam !== undefined &&
        currentUnitQueryParam !== null
      ) {
        unit = currentUnitQueryParam;
      }

      if (
        currentTypeUnitFilterAppliedCurrentDate === 'filters' &&
        currentUnitFilter !== undefined &&
        currentUnitFilter !== null
      ) {
        unit = currentUnitFilter !== '' ? Number(currentUnitFilter) : null;
      }

      if (currentUnit !== undefined) {
        unit = currentUnit !== '' ? Number(currentUnit) : null;
      }

      return unit;
    },
    [
      validateTabs,
      getFilterAppliedByKey,
      getValueQueryParams,
      getValueCurrentFilter,
      hasAccesLimitNotPro,
    ],
  );

  const parsePriceForRequest = useCallback(
    ({
      currentKey,
      currentPrice,
    }: {
      currentKey: 'priceGt' | 'priceLt';
      currentPrice?: string;
    }): Nullable<number> => {
      const currentTypePriceFilterAppliedCurrentDate =
        getFilterAppliedByKey(currentKey);

      const currentPriceQueryParam = getValueQueryParams(currentKey) ?? null;
      const currentPriceFilter = getValueCurrentFilter(currentKey);

      let price: Nullable<number> = null;

      if (
        currentTypePriceFilterAppliedCurrentDate === 'queryParams' &&
        currentPriceQueryParam !== undefined &&
        currentPriceQueryParam !== null
      ) {
        price = currentPriceQueryParam;
      }

      if (
        currentTypePriceFilterAppliedCurrentDate === 'filters' &&
        currentPriceFilter !== undefined &&
        currentPriceFilter !== null
      ) {
        price = currentPriceFilter !== '' ? Number(currentPriceFilter) : null;
      }

      if (currentPrice !== undefined) {
        price = currentPrice !== '' ? Number(currentPrice) : null;
      }

      return price;
    },
    [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilter],
  );

  const parseAssignedUserForRequest = useCallback(
    (currentAssignedUser?: Nullable<RouteUserBasic>) => {
      if (!hasAccesLimitNotPro) return null;

      const currentTypeAssignedUserFilterAppliedDrivers =
        getFilterAppliedByKey('assignedUser');

      const currentAssignedUserQueryParam = getValueQueryParams('assignedUser');
      const currentAssignedUserFilter = getValueCurrentFilter('assignedUser');

      let assignedUserId: Nullable<string> = null;

      if (
        currentTypeAssignedUserFilterAppliedDrivers === 'queryParams' &&
        currentAssignedUserQueryParam
      ) {
        assignedUserId = currentAssignedUserQueryParam;
      }

      if (
        currentTypeAssignedUserFilterAppliedDrivers === 'filters' &&
        currentAssignedUserFilter
      ) {
        assignedUserId = currentAssignedUserFilter.userId;
      }

      if (currentAssignedUser !== undefined) {
        assignedUserId = currentAssignedUser?.userId ?? null;
      }

      return assignedUserId;
    },
    [
      getFilterAppliedByKey,
      getValueCurrentFilter,
      getValueQueryParams,
      hasAccesLimitNotPro,
    ],
  );

  const parseFiltersRequest = useCallback(
    (currentFilters?: Partial<OrdersListFilters>): ItemFilter => ({
      dateFrom: parseDateForRequest({
        currentKey: 'dateFrom',
        excludeTabs: ['pending'],
        currentDate: currentFilters?.dateFrom,
      }),
      dateTo: parseDateForRequest({
        currentKey: 'dateTo',
        excludeTabs: ['pending'],
        currentDate: currentFilters?.dateTo,
      }),
      routeCode: parseSearchForRequest(currentFilters?.search),
      maxDeliveredDateTime: parseDateForRequest({
        currentKey: 'expirationDate',
        excludeTabs: ['finalized'],
        currentDate: currentFilters?.expirationDate,
      }),
      routeItemStateTypesId: parseRouteStatesForRequest(currentFilters?.state),
      collectItemStateTypesId: parseItemStateForRequest(
        currentFilters?.itemState,
      ),
      driversId: parseDriversForRequest(currentFilters?.drivers),
      vehiclesId: parseVehiclesForRequest(currentFilters?.vehicles),
      serviceTypeList: parseServicesForRequest(currentFilters?.services),
      zonesId: parseZonesForRequest(currentFilters?.zones),
      unit1GraterThan: parseUnitForRequest({
        currentKey: 'unit1Gt',
        excludeTabs: ['on-agenda', 'finalized'],
        currentUnit: currentFilters?.unit1Gt,
      }),
      unit1LowerThan: parseUnitForRequest({
        currentKey: 'unit1Lt',
        excludeTabs: ['on-agenda', 'finalized'],
        currentUnit: currentFilters?.unit1Lt,
      }),
      unit2GraterThan: parseUnitForRequest({
        currentKey: 'unit2Gt',
        excludeTabs: ['on-agenda', 'finalized'],
        currentUnit: currentFilters?.unit2Gt,
      }),
      unit2LowerThan: parseUnitForRequest({
        currentKey: 'unit2Lt',
        excludeTabs: ['on-agenda', 'finalized'],
        currentUnit: currentFilters?.unit2Lt,
      }),
      priceGraterThan: parsePriceForRequest({
        currentKey: 'priceGt',
        currentPrice: currentFilters?.priceGt,
      }),
      priceLowerThan: parsePriceForRequest({
        currentKey: 'priceLt',
        currentPrice: currentFilters?.priceLt,
      }),
      assignedUserId: parseAssignedUserForRequest(currentFilters?.assignedUser),
    }),
    [
      parseDateForRequest,
      parseSearchForRequest,
      parseRouteStatesForRequest,
      parseItemStateForRequest,
      parseDriversForRequest,
      parseVehiclesForRequest,
      parseServicesForRequest,
      parseZonesForRequest,
      parseUnitForRequest,
      parsePriceForRequest,
      parseAssignedUserForRequest,
    ],
  );

  return {
    ...restFiltersData,
    filters,
    setFilterByKey: setFilterAppliedByKey,
    handleChangeInputFilter: handleChangeInputFilterApplied,
    clearFilters: clearFiltersApplied,
    handleChangeFilters: handleChangeFiltersApplied,
    parseFiltersRequest,
    filtersActives,
    setFiltersActives,
    setFilterActiveByKey,
    clearFiltersActives,
  };
};
